import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const listPublishedMentors = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("lxp_mentor_profiles")
      .select("id,display_name,headline,bio,specialisations,years_experience,availability,hourly_rate_cents")
      .eq("published", true)
      .order("years_experience", { ascending: false, nullsFirst: false });
    if (error) throw new Error(error.message);
    return { mentors: data ?? [] };
  });

export const getMyMentorProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("lxp_mentor_profiles")
      .select("*")
      .eq("user_id", context.userId)
      .maybeSingle();
    return { profile: data ?? null };
  });

export const saveMyMentorProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      display_name: z.string().min(2).max(120),
      headline: z.string().max(200).optional(),
      bio: z.string().max(3000).optional(),
      specialisations: z.array(z.string().max(60)).max(20).default([]),
      years_experience: z.number().int().min(0).max(70).optional(),
      availability: z.string().max(200).optional(),
      hourly_rate_cents: z.number().int().min(0).max(1_000_000).optional(),
      published: z.boolean().default(false),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase.from("lxp_mentor_profiles").upsert(
      {
        user_id: userId,
        display_name: data.display_name,
        headline: data.headline ?? null,
        bio: data.bio ?? null,
        specialisations: data.specialisations,
        years_experience: data.years_experience ?? null,
        availability: data.availability ?? null,
        hourly_rate_cents: data.hourly_rate_cents ?? null,
        published: data.published,
      },
      { onConflict: "user_id" },
    );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const requestMentorSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      mentorId: z.string().uuid(),
      topic: z.string().min(4).max(200),
      studentNotes: z.string().max(2000).optional(),
      scheduledAt: z.string().datetime().optional(),
      durationMin: z.number().int().min(15).max(180).default(30),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase
      .from("lxp_mentor_sessions")
      .insert({
        mentor_id: data.mentorId,
        student_id: userId,
        topic: data.topic,
        student_notes: data.studentNotes ?? null,
        scheduled_at: data.scheduledAt ?? null,
        duration_min: data.durationMin,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

export const listMyMentorSessions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    // Mine as student
    const asStudent = await supabase
      .from("lxp_mentor_sessions")
      .select("*, mentor:lxp_mentor_profiles(display_name,headline)")
      .eq("student_id", userId)
      .order("created_at", { ascending: false });
    // Mine as mentor
    const { data: mp } = await supabase
      .from("lxp_mentor_profiles").select("id").eq("user_id", userId).maybeSingle();
    let asMentor: any = { data: [] };
    if (mp?.id) {
      asMentor = await supabase
        .from("lxp_mentor_sessions")
        .select("*")
        .eq("mentor_id", mp.id)
        .order("created_at", { ascending: false });
    }
    return { asStudent: asStudent.data ?? [], asMentor: asMentor.data ?? [] };
  });

export const updateMentorSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      id: z.string().uuid(),
      status: z.enum(["requested", "confirmed", "completed", "cancelled"]).optional(),
      meetingUrl: z.string().url().max(500).optional(),
      mentorNotes: z.string().max(3000).optional(),
      scheduledAt: z.string().datetime().optional(),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const patch: {
      status?: string;
      meeting_url?: string;
      mentor_notes?: string;
      scheduled_at?: string;
    } = {};
    if (data.status) patch.status = data.status;
    if (data.meetingUrl) patch.meeting_url = data.meetingUrl;
    if (data.mentorNotes) patch.mentor_notes = data.mentorNotes;
    if (data.scheduledAt) patch.scheduled_at = data.scheduledAt;
    const { error } = await supabase.from("lxp_mentor_sessions").update(patch).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const createMentorFeedback = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      studentId: z.string().uuid(),
      submissionId: z.string().uuid().optional(),
      evidenceId: z.string().uuid().optional(),
      strengths: z.string().max(2000).optional(),
      improvements: z.string().max(2000).optional(),
      guidance: z.string().max(2000).optional(),
      rating: z.number().int().min(1).max(5).optional(),
      rubric: z.record(z.string(), z.number().int().min(0).max(5)).optional(),
      resubmissionRequested: z.boolean().default(false),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: mp } = await supabase
      .from("lxp_mentor_profiles").select("id").eq("user_id", userId).maybeSingle();
    if (!mp?.id) throw new Error("Only mentors can leave feedback");
    const { error } = await supabase.from("lxp_mentor_feedback").insert({
      mentor_id: mp.id,
      student_id: data.studentId,
      submission_id: data.submissionId ?? null,
      evidence_id: data.evidenceId ?? null,
      strengths: data.strengths ?? null,
      improvements: data.improvements ?? null,
      guidance: data.guidance ?? null,
      rating: data.rating ?? null,
      rubric_scores: data.rubric ?? null,
      resubmission_requested_at: data.resubmissionRequested ? new Date().toISOString() : null,
    });
    if (error) throw new Error(error.message);
    if (data.resubmissionRequested && data.submissionId) {
      await supabase.from("lxp_firm_submissions")
        .update({ status: "resubmit_requested" })
        .eq("id", data.submissionId);
    }
    return { ok: true };
  });

